import * as React from "react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  getAdminGrants,
  getAdminRunGrants,
  getMyAdminStatus,
  grantCreditsAdmin,
  grantRunsAdmin,
} from "@/lib/billing.functions";
import { grantFreeDeckStudioReportsAdmin } from "@/lib/deck-studio.functions";

export const Route = createFileRoute("/_authed/admin/credits")({
  head: () => ({
    meta: [
      { title: "Admin credits: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: async () => {
    if (typeof window === "undefined") return;
    const status = await getMyAdminStatus();
    if (!status.isAdmin) throw redirect({ to: "/dashboard" });
  },
  component: AdminCreditsPage,
});

function AdminCreditsPage() {
  const qc = useQueryClient();
  const fetchGrants = useServerFn(getAdminGrants);
  const grant = useServerFn(grantCreditsAdmin);
  const fetchRunGrants = useServerFn(getAdminRunGrants);
  const grantRunsFn = useServerFn(grantRunsAdmin);
  const grantDsFn = useServerFn(grantFreeDeckStudioReportsAdmin);

  const grants = useQuery({
    queryKey: ["admin-grants"],
    queryFn: () => fetchGrants(),
  });
  const runGrants = useQuery({
    queryKey: ["admin-run-grants"],
    queryFn: () => fetchRunGrants(),
  });

  const [email, setEmail] = React.useState("");
  const [amount, setAmount] = React.useState("100");
  const [reason, setReason] = React.useState("");
  const [success, setSuccess] = React.useState<string | null>(null);

  const [rEmail, setREmail] = React.useState("");
  const [rAmount, setRAmount] = React.useState("1");
  const [rReason, setRReason] = React.useState("");
  const [rSuccess, setRSuccess] = React.useState<string | null>(null);

  const [dsEmail, setDsEmail] = React.useState("");
  const [dsAmount, setDsAmount] = React.useState("1");
  const [dsSuccess, setDsSuccess] = React.useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      grant({
        data: {
          target_email: email,
          amount: parseInt(amount, 10) || 0,
          reason: reason.trim() || null,
        },
      }),
    onSuccess: () => {
      setSuccess(`Granted ${amount} credits to ${email}`);
      setEmail("");
      setReason("");
      qc.invalidateQueries({ queryKey: ["admin-grants"] });
    },
  });

  const runMutation = useMutation({
    mutationFn: () =>
      grantRunsFn({
        data: {
          target_email: rEmail,
          amount: parseInt(rAmount, 10) || 0,
          reason: rReason.trim() || null,
        },
      }),
    onSuccess: () => {
      setRSuccess(`Granted ${rAmount} runs to ${rEmail}`);
      setREmail("");
      setRReason("");
      qc.invalidateQueries({ queryKey: ["admin-run-grants"] });
    },
  });

  const dsMutation = useMutation({
    mutationFn: () =>
      grantDsFn({
        data: {
          target_email: dsEmail,
          amount: parseInt(dsAmount, 10) || 0,
        },
      }),
    onSuccess: (res) => {
      setDsSuccess(
        `Granted ${dsAmount} free Investor Readiness Report(s) to ${dsEmail}. New balance: ${res.free_remaining}.`,
      );
      setDsEmail("");
    },
  });

  return (
    <main className="mx-auto max-w-[720px] px-4 pb-16 pt-6">
      <header className="mb-6">
        <Link
          to="/admin"
          className="text-[12.5px] text-brand-muted no-underline hover:text-brand-ink"
        >
          ← Back to admin
        </Link>
        <h1 className="mt-2 font-serif text-[32px] font-normal leading-tight tracking-tight text-brand-ink">
          Credits.
        </h1>
        <p className="mt-1 text-[14px] text-brand-muted">
          Grant credits, workflow allowances, and free Investor Readiness
          Reports. For the full mutation history, see the{" "}
          <Link
            to="/admin/ledger"
            className="text-brand-ink underline hover:no-underline"
          >
            credit ledger
          </Link>
          .
        </p>
      </header>

      <section className="mb-8 rounded-2xl border border-brand-line bg-brand-paper p-5">
        <h2 className="mb-4 font-serif text-[20px] text-brand-ink">
          Grant credits
        </h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSuccess(null);
            mutation.mutate();
          }}
          className="space-y-3"
        >
          <div>
            <Label htmlFor="g-email">Target email</Label>
            <Input
              id="g-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
            />
          </div>
          <div>
            <Label htmlFor="g-amount">Amount</Label>
            <Input
              id="g-amount"
              type="number"
              min={1}
              max={10000}
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="g-reason">Reason (optional)</Label>
            <Textarea
              id="g-reason"
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. comp / testing / makegood"
            />
          </div>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? "Granting…" : "Grant credits"}
          </Button>
          {mutation.error && (
            <p className="text-[12.5px] text-destructive">
              {mutation.error instanceof Error
                ? mutation.error.message
                : "Grant failed"}
            </p>
          )}
          {success && (
            <p className="text-[12.5px] text-[oklch(0.45_0.087_155.7)]">
              {success}
            </p>
          )}
        </form>
      </section>

      <section className="mb-8 rounded-2xl border border-brand-line bg-brand-paper p-5">
        <h2 className="mb-4 font-serif text-[20px] text-brand-ink">
          Grant runs
        </h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setRSuccess(null);
            runMutation.mutate();
          }}
          className="space-y-3"
        >
          <div>
            <Label htmlFor="r-email">Target email</Label>
            <Input
              id="r-email"
              type="email"
              required
              value={rEmail}
              onChange={(e) => setREmail(e.target.value)}
              placeholder="user@example.com"
            />
          </div>
          <div>
            <Label htmlFor="r-amount">Amount</Label>
            <Input
              id="r-amount"
              type="number"
              min={1}
              max={1000}
              required
              value={rAmount}
              onChange={(e) => setRAmount(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="r-reason">Reason (optional)</Label>
            <Textarea
              id="r-reason"
              maxLength={500}
              value={rReason}
              onChange={(e) => setRReason(e.target.value)}
              placeholder="e.g. comp / testing / makegood"
            />
          </div>
          <Button type="submit" disabled={runMutation.isPending}>
            {runMutation.isPending ? "Granting…" : "Grant runs"}
          </Button>
          {runMutation.error && (
            <p className="text-[12.5px] text-destructive">
              {runMutation.error instanceof Error
                ? runMutation.error.message
                : "Grant failed"}
            </p>
          )}
          {rSuccess && (
            <p className="text-[12.5px] text-[oklch(0.45_0.087_155.7)]">
              {rSuccess}
            </p>
          )}
        </form>
      </section>

      <section className="mb-8 rounded-2xl border border-brand-line bg-brand-paper p-5">
        <h2 className="mb-1 font-serif text-[20px] text-brand-ink">
          Grant Investor Readiness Reports
        </h2>
        <p className="mb-4 text-[12.5px] text-brand-muted">
          Bumps{" "}
          <code className="font-mono">
            user_credits.free_deck_studio_reports_remaining
          </code>{" "}
          only. Does not touch credits or workflow allowances. Use for testing
          the free redemption path without going through Stripe.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setDsSuccess(null);
            dsMutation.mutate();
          }}
          className="space-y-3"
        >
          <div>
            <Label htmlFor="ds-email">Target email</Label>
            <Input
              id="ds-email"
              type="email"
              required
              value={dsEmail}
              onChange={(e) => setDsEmail(e.target.value)}
              placeholder="user@example.com"
            />
          </div>
          <div>
            <Label htmlFor="ds-amount">Amount</Label>
            <Input
              id="ds-amount"
              type="number"
              min={1}
              max={50}
              required
              value={dsAmount}
              onChange={(e) => setDsAmount(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={dsMutation.isPending}>
            {dsMutation.isPending ? "Granting…" : "Grant free reports"}
          </Button>
          {dsMutation.error && (
            <p className="text-[12.5px] text-destructive">
              {dsMutation.error instanceof Error
                ? dsMutation.error.message
                : "Grant failed"}
            </p>
          )}
          {dsSuccess && (
            <p className="text-[12.5px] text-[oklch(0.45_0.087_155.7)]">
              {dsSuccess}
            </p>
          )}
        </form>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 font-serif text-[20px] text-brand-ink">
          Recent grants
        </h2>
        {grants.isLoading ? (
          <p className="text-[14px] text-brand-muted">Loading…</p>
        ) : !grants.data || grants.data.length === 0 ? (
          <p className="text-[14px] text-brand-muted">No grants yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-brand-line bg-brand-paper">
            <table className="w-full text-[14px]">
              <thead className="bg-brand-paper-2 text-left text-[11.5px] uppercase tracking-wider text-brand-muted">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Admin</th>
                  <th className="px-3 py-2">Target user</th>
                  <th className="px-3 py-2">Amount</th>
                  <th className="px-3 py-2">Reason</th>
                </tr>
              </thead>
              <tbody>
                {grants.data.map((g) => (
                  <tr key={g.id} className="border-t border-brand-line">
                    <td className="px-3 py-2 text-brand-muted">
                      {new Date(g.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2">{g.admin_email}</td>
                    <td className="px-3 py-2">
                      <div>{g.target_email ?? "—"}</div>
                      <div className="font-mono text-[11px] text-brand-muted">
                        {g.target_user_id.slice(0, 8)}…
                      </div>
                    </td>
                    <td className="px-3 py-2 tabular-nums">{g.amount}</td>
                    <td className="px-3 py-2 text-brand-muted">
                      {g.reason ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-3 font-serif text-[20px] text-brand-ink">
          Recent workflow grants
        </h2>
        {runGrants.isLoading ? (
          <p className="text-[14px] text-brand-muted">Loading…</p>
        ) : !runGrants.data || runGrants.data.length === 0 ? (
          <p className="text-[14px] text-brand-muted">No workflow grants yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-brand-line bg-brand-paper">
            <table className="w-full text-[14px]">
              <thead className="bg-brand-paper-2 text-left text-[11.5px] uppercase tracking-wider text-brand-muted">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Admin</th>
                  <th className="px-3 py-2">Target user</th>
                  <th className="px-3 py-2">Amount</th>
                  <th className="px-3 py-2">Reason</th>
                </tr>
              </thead>
              <tbody>
                {runGrants.data.map((g) => (
                  <tr key={g.id} className="border-t border-brand-line">
                    <td className="px-3 py-2 text-brand-muted">
                      {new Date(g.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2">{g.admin_email}</td>
                    <td className="px-3 py-2">
                      <div>{g.target_email ?? "—"}</div>
                      <div className="font-mono text-[11px] text-brand-muted">
                        {g.target_user_id.slice(0, 8)}…
                      </div>
                    </td>
                    <td className="px-3 py-2 tabular-nums">{g.amount}</td>
                    <td className="px-3 py-2 text-brand-muted">
                      {g.reason ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}